import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form, Row, Col, Alert, InputGroup } from '@/components/ui';
import { Project, ProjectType, ProjectStatus } from '../../../types/Project';
import { User } from '../../../types/User';
import { usersService } from '../../../services/usersService';
import { useAuth } from '../../../contexts/AuthContext';
import { areasService, ServiceArea, ContractArea } from '../../../services/areasService';
import { TaskStage } from '../../../types/Task';
import { usePermissions } from '../../../hooks/usePermissions';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';
import MultiTierDropdown from '../../../components/MultiTierDropdown';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import { Asset } from '../../../types/Asset';
import { assetService } from '../../../services/assetService';
import { sortAssetsAlphabetically } from '../../../utils/assetUtils';
import { formatDollars } from '../../../utils/formatDollars';
import { describeActionError } from '../../../utils/loadError';

interface ProjectModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (project: Partial<Project>, h200FormFile?: File) => Promise<void>;
  project?: Project;
}

const JOB_NUMBER_REQUIRED_TO_COMPLETE = 'Job number required to complete an H200 or CMC/MVI project';

const PROJECT_TYPES = [
  { value: 'H200', label: 'H200' },
  { value: 'Internal', label: 'Internal' },
  { value: 'Other Revenue', label: 'Other Revenue' },
  { value: 'Routine', label: 'Routine' },
  { value: 'Quantified', label: 'Quantified' },
  { value: 'CMC/MVI', label: 'CMC/MVI' }
];

// Add the same SERVICE_AREA_MARKUPS constant as used in TaskModal
const SERVICE_AREA_MARKUPS = {
  '22': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '16': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '25': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  },
  '17': {
    labourMarkup: 60,
    materialMarkup: 10,
    vehicleMarkup: 15,
    subcontractorMarkup: 10,
    hiredEquipmentMarkup: 15
  }
};

/** A contract area belongs to a service area through any of its foreman areas. */
const isInServiceArea = (contractArea: ContractArea, serviceAreaId: number): boolean =>
  !!contractArea.foremanAreas?.some(fa => Number(fa.serviceAreaId ?? fa.serviceArea?.id) === serviceAreaId);

const ProjectModal: React.FC<ProjectModalProps> = ({
  visible,
  onClose,
  onSave,
  project
}) => {
  // Helper function to safely format date for input field
  const formatDateForInput = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canCancelProject = hasPermission('project:cancel');
  const canCloseProject = hasPermission('project:close');
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState<Partial<Project>>({
    name: '',
    description: '',
    startDate: undefined,
    endDate: undefined,
    status: 'active',
    ownerId: currentUser?.id,
    serviceAreaId: undefined,
    type: 'Quantified' as ProjectType,
    projectNumber: '',
    jobNumber: '',
    budget: 0,
    labourMarkup: 0,
    materialMarkup: 0,
    vehicleMarkup: 0,
    subcontractorMarkup: 0,
    hiredEquipmentMarkup: 0,
    // H200 specific fields
    h200ActivityType: '',
    h200Number: '',
    h200ContractAreaId: undefined,
    h200AssetType: '',
    h200AssetId: undefined,
    h200StartLatitude: undefined,
    h200StartLongitude: undefined,
    h200EndLatitude: undefined,
    h200EndLongitude: undefined,
    h200FormAttachmentUrl: '',
    h200DeductibleApplies: false,
    h200DeductibleAmount: undefined as number | undefined
  });
  
  // Debug initial form data
  const [users, setUsers] = useState<User[]>([]);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [hasActiveTasks, setHasActiveTasks] = useState(false);
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [roadAssets, setRoadAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [locationType, setLocationType] = useState<'start' | 'end'>('start');
  const [selectedH200FormFile, setSelectedH200FormFile] = useState<File | null>(null);
  const [jobNumberValidation, setJobNumberValidation] = useState<{ isValid: boolean; message: string }>({ isValid: true, message: '' });
  // Inline feedback (FORM-01 / STATE-06) in place of browser alerts.
  const [statusError, setStatusError] = useState<string | null>(null);
  const [jobNumberRequiredError, setJobNumberRequiredError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [markupsReviewed, setMarkupsReviewed] = useState(false);
  // Snapshot of the markup values as of the last time they were considered "reviewed"
  // (i.e. the values already saved on the project). Used to detect whether the user
  // has changed anything on the Markups tab since opening the modal.
  const [markupsBaseline, setMarkupsBaseline] = useState<{
    labourMarkup: number;
    materialMarkup: number;
    vehicleMarkup: number;
    subcontractorMarkup: number;
    hiredEquipmentMarkup: number;
  } | null>(null);

  useEffect(() => {
    // Check for active tasks when status changes to cancelled
    if (project?.tasks && formData.status === 'cancelled') {
      const activeTasks = project.tasks.some(task => 
        task.status !== TaskStage.CANCELLED
      );
      setHasActiveTasks(activeTasks);
    } else {
      setHasActiveTasks(false);
    }
  }, [formData.status, project?.tasks]);

  useEffect(() => {
    // Fetch service areas when modal opens
    const fetchServiceAreas = async () => {
      try {
        const serviceAreaData = await areasService.getServiceAreas();
        setServiceAreas(serviceAreaData);
      } catch (error) {
        console.error('Error fetching service areas:', error);
      }
    };

    if (visible) {
      fetchServiceAreas();
    }
  }, [visible]);

  useEffect(() => {
    // Fetch users based on selected service area
    const fetchUsers = async () => {
      try {
        let userData;
        if (formData.serviceAreaId) {
          // Get users filtered by service area (skip Redis cache to prevent crashes)
          userData = await usersService.getUsersByServiceArea(
            formData.serviceAreaId, 
            1, 
            1000, 
            { status: 'active' }, // Only get active users
            true // skipCache: true
          );
        } else {
          // Fallback to all users if no service area selected (skip Redis cache to prevent crashes)
          userData = await usersService.getUsers(1, 1000, { status: 'active' }, true); // skipCache: true
        }
        
        setUsers(userData.users);
        
        // If we don't have an ownerId set yet, set it to the current user
        // But only if the current user is in the filtered list
        if (!formData.ownerId && currentUser?.id) {
          const userInList = userData.users.find(user => user.id === currentUser.id);
          if (userInList) {
            setFormData(prev => ({
              ...prev,
              ownerId: currentUser.id
            }));
          }
        }
        
        // If the currently selected owner is not in the filtered list, clear it
        if (formData.ownerId) {
          const ownerInList = userData.users.find(user => user.id === formData.ownerId);
          if (!ownerInList) {
            setFormData(prev => ({
              ...prev,
              ownerId: undefined
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (visible) {
      fetchUsers();
    }
  }, [visible, formData.serviceAreaId, currentUser?.id]);

  useEffect(() => {
    
    // Existing projects have already had their (saved) markups reviewed. Record a
    // baseline snapshot so we can tell whether the Markups tab is edited later —
    // the "reviewed" checkbox effect below keeps the checkmark checked as long as
    // the values still match this baseline.
    if (project) {
      const newFormData = {
        id: project.id, // Include the project ID
        name: project.name,
        description: project.description,
        startDate: project.startDate,
        endDate: project.endDate,
        status: project.status,
        ownerId: project.ownerId,
        serviceAreaId: project.serviceAreaId,
        type: project.type,
        projectNumber: project.projectNumber || '',
        jobNumber: project.jobNumber || '',
        budget: project.budget || 0,
        labourMarkup: project.labourMarkup || 0,
        materialMarkup: project.materialMarkup || 0,
        vehicleMarkup: project.vehicleMarkup || 0,
        subcontractorMarkup: project.subcontractorMarkup || 0,
        hiredEquipmentMarkup: project.hiredEquipmentMarkup || 0,
        // H200 specific fields
        h200ActivityType: project.h200ActivityType || '',
        h200Number: project.h200Number || '',
        h200ContractAreaId: project.h200ContractAreaId || undefined,
        h200AssetType: project.h200AssetType || '',
        h200AssetId: project.h200AssetId || undefined,
        h200StartLatitude: project.h200StartLatitude || undefined,
        h200StartLongitude: project.h200StartLongitude || undefined,
        h200EndLatitude: project.h200EndLatitude || undefined,
        h200EndLongitude: project.h200EndLongitude || undefined,
        h200FormAttachmentUrl: project.h200FormAttachmentUrl || '',
        h200DeductibleApplies: project.h200DeductibleApplies === true,
        h200DeductibleAmount:
          project.h200DeductibleAmount != null && !Number.isNaN(Number(project.h200DeductibleAmount))
            ? Number(project.h200DeductibleAmount)
            : undefined
      };
      
      setFormData(newFormData);

      setMarkupsBaseline({
        labourMarkup: newFormData.labourMarkup,
        materialMarkup: newFormData.materialMarkup,
        vehicleMarkup: newFormData.vehicleMarkup,
        subcontractorMarkup: newFormData.subcontractorMarkup,
        hiredEquipmentMarkup: newFormData.hiredEquipmentMarkup
      });
      
      // Validate job number if it exists
      if (newFormData.jobNumber) {
        const validation = validateJobNumber(newFormData.jobNumber);
        setJobNumberValidation(validation);
      }
      
    } else {
      const defaultFormData = {
        name: '',
        description: '',
        startDate: undefined,
        endDate: undefined,
        status: 'planned' as ProjectStatus,
        ownerId: undefined, // Don't auto-set owner for new projects, let user select service area first
        serviceAreaId: undefined,
        type: 'Quantified' as ProjectType,
        projectNumber: '',
        jobNumber: '',
        budget: 0,
        labourMarkup: 0,
        materialMarkup: 0,
        vehicleMarkup: 0,
        subcontractorMarkup: 0,
        hiredEquipmentMarkup: 0,
        // H200 specific fields
        h200ActivityType: '',
        h200Number: '',
        h200ContractAreaId: undefined,
        h200AssetType: '',
        h200AssetId: undefined,
        h200StartLatitude: undefined,
        h200StartLongitude: undefined,
        h200EndLatitude: undefined,
        h200EndLongitude: undefined,
        h200FormAttachmentUrl: '',
        h200DeductibleApplies: false,
        h200DeductibleAmount: undefined
      };
      setFormData(defaultFormData);
      // New projects have no saved markups to fall back on, so there is no baseline
      // to compare against — the checkbox must be checked manually.
      setMarkupsBaseline(null);
      setMarkupsReviewed(false);
    }
  }, [project, currentUser]);

  useEffect(() => {
    const fetchContractAreas = async () => {
      try {
        const contractAreasData = await areasService.getAllContractAreas();
        setContractAreas(contractAreasData);
      } catch (error) {
        console.error('Error fetching contract areas:', error);
      }
    };

    if (visible) {
      fetchContractAreas();
    }
  }, [visible]);

  // Contract areas offered for H200: only those in the project's service area. A saved project
  // whose contract area sits outside it (legacy data) keeps that option so editing doesn't drop it.
  const h200ContractAreaOptions = useMemo(() => {
    if (!formData.serviceAreaId) return [];
    const inServiceArea = contractAreas.filter(ca => isInServiceArea(ca, Number(formData.serviceAreaId)));
    const selected = contractAreas.find(ca => ca.id === formData.h200ContractAreaId);
    return selected && !inServiceArea.includes(selected) ? [...inServiceArea, selected] : inServiceArea;
  }, [contractAreas, formData.serviceAreaId, formData.h200ContractAreaId]);

  // Function to load assets for a specific contract area
  const loadAssetsForContractArea = useCallback(async (contractAreaId: number, assetType?: string) => {
    const effectiveType = assetType || formData.h200AssetType;
    try {

      if (effectiveType?.toLowerCase() === 'road') {
        const assetData = await assetService.searchAssetsLight({
          contractAreaId,
          assetType: 'road'
        });
        setAssets(sortAssetsAlphabetically(assetData));
        setRoadAssets([]); // No parent lookup for roads
        return;
      }

      // Non-road: fetch both in parallel (efficient - 2 requests, no sequencing)
      const [assetData, roadData] = await Promise.all([
        assetService.searchAssetsLight({ contractAreaId, assetType: effectiveType }),
        assetService.searchAssetsLight({ contractAreaId, assetType: 'road' })
      ]);

      const validRoads = Array.isArray(roadData)
        ? roadData.filter(a => a.assetType?.toLowerCase() === 'road')
        : [];

      setAssets(sortAssetsAlphabetically(assetData));
      setRoadAssets(sortAssetsAlphabetically(validRoads));
    } catch (error) {
      console.error('Error loading assets for contract area:', error);
      setAssets([]);
      setRoadAssets([]);
    }
  }, [formData.h200AssetType]);

  useEffect(() => {
    if (visible && formData.h200ContractAreaId && formData.h200AssetType) {
      loadAssetsForContractArea(formData.h200ContractAreaId, formData.h200AssetType);
    } else if (visible) {
      setAssets([]);
      setRoadAssets([]);
    }
  }, [visible, formData.h200ContractAreaId, formData.h200AssetType, loadAssetsForContractArea]);

  // Add effect to load assets for existing project
  useEffect(() => {
    if (project && project.h200ContractAreaId && project.h200AssetType) {
      
      // Load assets for the contract area using lightweight search
      loadAssetsForContractArea(project.h200ContractAreaId, project.h200AssetType);
    }
  }, [project, loadAssetsForContractArea]);

  // Separate effect to load assets when form data is ready for existing projects
  useEffect(() => {
    if (project && formData.h200ContractAreaId && formData.h200AssetType && visible) {
      
      // Load assets for the contract area using lightweight search
      loadAssetsForContractArea(formData.h200ContractAreaId, formData.h200AssetType);
    }
  }, [project, formData.h200ContractAreaId, formData.h200AssetType, visible, loadAssetsForContractArea]);

  // Add effect to set selected asset when assets are loaded and project has asset ID
  useEffect(() => {
    if (project?.h200AssetId && assets.length > 0) {
      const projectAsset = assets.find(asset => asset.id === project.h200AssetId);
      if (projectAsset) {
        setSelectedAsset(projectAsset);
      }
    }
  }, [project?.h200AssetId, assets]);

  // Remove auto-switching to H200 tab - always default to General tab

  // Remove the old asset type change effect since we handle it in the change handler now

  useEffect(() => {
    if (formData.h200AssetType && selectedAsset) {
      if (selectedAsset.startLatitude && selectedAsset.startLongitude) {
        setFormData(prev => ({
          ...prev,
          h200StartLatitude: selectedAsset.startLatitude,
          h200StartLongitude: selectedAsset.startLongitude,
          h200EndLatitude: selectedAsset.endLatitude || selectedAsset.startLatitude,
          h200EndLongitude: selectedAsset.endLongitude || selectedAsset.startLongitude
        }));
      }
    }
  }, [formData.h200AssetType, selectedAsset]);

  // Add effect to track modal visibility and project data
  useEffect(() => {
    if (visible) {
      // Only reset to general tab when modal first becomes visible (not when form data changes)
      setActiveTab('general');
      // Reset selected file when modal becomes visible
      setSelectedH200FormFile(null);
      // Note: the markups-reviewed checkbox is intentionally NOT reset here. Whether it's
      // checked is derived from comparing the current markup values against the saved
      // baseline (see the effect below), so re-opening the modal without changing the
      // Markups tab keeps it checked instead of forcing the user to re-confirm every time.
    }
  }, [visible]); // Remove project and formData dependencies to prevent tab switching on form changes

  // Keep the "markups reviewed" checkbox in sync with whether the Markups tab values
  // still match the saved baseline. If the user edits any markup field (directly, or via
  // the service-area/type default-markup logic), the checkbox unchecks and they must
  // re-confirm; if they revert back to the saved values, it re-checks automatically.
  useEffect(() => {
    if (!markupsBaseline) {
      return;
    }
    const matchesBaseline =
      (formData.labourMarkup ?? 0) === markupsBaseline.labourMarkup &&
      (formData.materialMarkup ?? 0) === markupsBaseline.materialMarkup &&
      (formData.vehicleMarkup ?? 0) === markupsBaseline.vehicleMarkup &&
      (formData.subcontractorMarkup ?? 0) === markupsBaseline.subcontractorMarkup &&
      (formData.hiredEquipmentMarkup ?? 0) === markupsBaseline.hiredEquipmentMarkup;
    setMarkupsReviewed(matchesBaseline);
  }, [
    markupsBaseline,
    formData.labourMarkup,
    formData.materialMarkup,
    formData.vehicleMarkup,
    formData.subcontractorMarkup,
    formData.hiredEquipmentMarkup
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Prevent changing status to "completed" if job number is missing for H200 or CMC/MVI
    if (name === 'status' && value === 'completed') {
      const requiresJobNumber = formData.type === 'H200' || formData.type === 'CMC/MVI';
      if (requiresJobNumber && !formData.jobNumber) {
        setStatusError(JOB_NUMBER_REQUIRED_TO_COMPLETE);
        // Reset the select to previous value
        (e.target as HTMLSelectElement).value = formData.status || 'planned';
        return;
      }
    }
    
    if (name === 'status' && statusError) setStatusError(null);

    // Handle job number field validation without auto-formatting
    if (name === 'jobNumber') {
      if (value) {
        setJobNumberRequiredError(null);
        setStatusError(null);
      }
      // Validate job number format
      const validation = validateJobNumber(value);
      setJobNumberValidation(validation);
    }
    
    // Special handling for service area changes to apply default markups
    if (name === 'serviceAreaId') {
      const serviceAreaId = value;
      const defaultMarkups = serviceAreaId ? SERVICE_AREA_MARKUPS[serviceAreaId as keyof typeof SERVICE_AREA_MARKUPS] : null;

      // The H200 contract area list is scoped to the service area, so a contract area from the
      // previous service area (and the asset picked inside it) no longer applies.
      const currentContractArea = contractAreas.find(ca => ca.id === formData.h200ContractAreaId);
      const dropContractArea =
        formData.h200ContractAreaId != null &&
        !(currentContractArea && isInServiceArea(currentContractArea, Number(value)));
      if (dropContractArea) {
        setSelectedAsset(null);
        setAssets([]);
        setRoadAssets([]);
      }

      setFormData(prev => ({
        ...prev,
        serviceAreaId: Number(value),
        ...(dropContractArea && {
          h200ContractAreaId: undefined,
          h200AssetType: '',
          h200AssetId: undefined,
          h200StartLatitude: undefined,
          h200StartLongitude: undefined,
          h200EndLatitude: undefined,
          h200EndLongitude: undefined
        }),
        // Apply default markups if they exist and project type supports markups
        ...(defaultMarkups && (prev.type === 'H200' || prev.type === 'CMC/MVI' || prev.type === 'Other Revenue') && {
          labourMarkup: defaultMarkups.labourMarkup,
          materialMarkup: defaultMarkups.materialMarkup,
          vehicleMarkup: defaultMarkups.vehicleMarkup,
          subcontractorMarkup: defaultMarkups.subcontractorMarkup,
          hiredEquipmentMarkup: defaultMarkups.hiredEquipmentMarkup
        })
      }));
      return;
    }
    
    // Special handling for project type changes
    if (name === 'type') {
      const newType = value as ProjectType;
      const showMarkups = newType === 'H200' || newType === 'CMC/MVI' || newType === 'Other Revenue';
      const isQuantified = newType === 'Quantified';

      if (isQuantified) {
        setJobNumberValidation({ isValid: true, message: '' });
      }

      if (showMarkups && formData.serviceAreaId) {
        // Apply default markups when switching to a type that supports markups
        const defaultMarkups = SERVICE_AREA_MARKUPS[formData.serviceAreaId.toString() as keyof typeof SERVICE_AREA_MARKUPS];
        
        setFormData(prev => ({
          ...prev,
          type: newType,
          ...(isQuantified && { jobNumber: '' }),
          ...(newType !== 'H200'
            ? { h200DeductibleApplies: false, h200DeductibleAmount: undefined }
            : {}),
          ...(defaultMarkups && {
            labourMarkup: defaultMarkups.labourMarkup,
            materialMarkup: defaultMarkups.materialMarkup,
            vehicleMarkup: defaultMarkups.vehicleMarkup,
            subcontractorMarkup: defaultMarkups.subcontractorMarkup,
            hiredEquipmentMarkup: defaultMarkups.hiredEquipmentMarkup
          })
        }));
        return;
      } else {
        // Reset markups to 0 when switching to a type that doesn't support markups
        setFormData(prev => ({
          ...prev,
          type: newType,
          ...(isQuantified && { jobNumber: '' }),
          ...(newType !== 'H200'
            ? { h200DeductibleApplies: false, h200DeductibleAmount: undefined }
            : {}),
          labourMarkup: 0,
          materialMarkup: 0,
          vehicleMarkup: 0,
          subcontractorMarkup: 0,
          hiredEquipmentMarkup: 0
        }));
        return;
      }
    }
    
    // Handle number inputs - convert to number or clear optional empties
    const numberFields = [
      'budget',
      'labourMarkup',
      'materialMarkup',
      'vehicleMarkup',
      'subcontractorMarkup',
      'hiredEquipmentMarkup',
      'h200StartLatitude',
      'h200StartLongitude',
      'h200EndLatitude',
      'h200EndLongitude',
      'h200DeductibleAmount'
    ];
    // Optional numeric fields: empty input should clear (null), not coerce to 0.
    // Use null (not undefined) so JSON includes the key and the server can clear the value.
    const optionalClearableNumberFields = [
      'h200EndLatitude',
      'h200EndLongitude',
      'h200DeductibleAmount'
    ];
    if (numberFields.includes(name)) {
      if (value === '') {
        setFormData(prev => ({
          ...prev,
          [name]: optionalClearableNumberFields.includes(name) ? null : 0
        }));
        return;
      }
      const numValue = parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? 0 : numValue
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Job number validation function
  const validateJobNumber = (jobNumber: string): { isValid: boolean; message: string } => {
    if (!jobNumber) {
      return { isValid: true, message: '' }; // Empty is valid
    }
    
    // Regex pattern: XXXx-YYY-ZZZZ
    // XXXx: 3-4 alphanumeric characters
    // YYY: 3 alphanumeric characters (fixed length)
    // ZZZZ: 4 alphanumeric characters (fixed length)
    const jobNumberRegex = /^[a-zA-Z0-9]{3,4}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{4}$/;
    
    if (jobNumberRegex.test(jobNumber)) {
      return { isValid: true, message: '' };
    } else {
      return { 
        isValid: false, 
        message: 'Job number must follow format: XXXx-YYY-ZZZZ where all characters must be alphanumeric and XXXx can be either 3 or 4 characters (e.g., ABC1-123-12DE or 1234-5E6-78FG)' 
      };
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate job number requirement for H200 and CMC/MVI when status is completed
    if (formData.status === 'completed') {
      const requiresJobNumber = formData.type === 'H200' || formData.type === 'CMC/MVI';
      if (requiresJobNumber && !formData.jobNumber) {
        setActiveTab('general');
        setJobNumberRequiredError(JOB_NUMBER_REQUIRED_TO_COMPLETE);
        setTimeout(() => document.getElementById('projectJobNumber')?.focus(), 0);
        return;
      }
    }
    
    setSaveError(null);
    try {
      // Ensure ownerId is a number since the API expects it
      const projectData = {
        ...formData,
        ownerId: formData.ownerId ? parseInt(formData.ownerId.toString()) : undefined
      };
      
      // Pass the H200 form file if one is selected
      await onSave(projectData, selectedH200FormFile || undefined);
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
      setSaveError(describeActionError(error, "Couldn't save the project. Try again."));
    }
  };

  // End before start is always invalid. Compare the date-only values so a same-day range,
  // and any time component the API may have attached, both stay valid.
  const isDateRangeInverted = (() => {
    if (!formData.startDate || !formData.endDate) return false;
    const start = formatDateForInput(formData.startDate);
    const end = formatDateForInput(formData.endDate);
    if (!start || !end) return false;
    return end < start;
  })();

  const isSubmitDisabled = () => {
    if (formData.status === 'cancelled' && hasActiveTasks) {
      return true;
    }

    // Check mandatory fields (budget may be 0 — do not treat as missing)
    if (
      !formData.name ||
      !formData.serviceAreaId ||
      !formData.ownerId ||
      !formData.type ||
      formData.budget === undefined ||
      formData.budget === null
    ) {
      return true;
    }

    // Check date fields when status is active
    if (formData.status === 'active' && (!formData.startDate || !formData.endDate)) {
      return true;
    }

    // Whenever both dates are present they must be in order, regardless of status.
    if (isDateRangeInverted) {
      return true;
    }

    // Check job number validation if one is provided
    if (formData.jobNumber && !jobNumberValidation.isValid) {
      return true;
    }

    // Prevent completing H200 or CMC/MVI projects without a job number
    if (formData.status === 'completed') {
      const requiresJobNumber = formData.type === 'H200' || formData.type === 'CMC/MVI';
      if (requiresJobNumber && !formData.jobNumber) {
        return true;
      }
    }

    // Check H200-specific required fields
    if (formData.type === 'H200') {
      if (!formData.h200ActivityType || 
          !formData.h200ContractAreaId || 
          !formData.h200AssetType || 
          !formData.h200AssetId || 
          !formData.h200StartLatitude || 
          !formData.h200StartLongitude) {
        return true;
      }
      if (formData.h200DeductibleApplies) {
        const amt = Number(formData.h200DeductibleAmount);
        if (!(Number.isFinite(amt) && amt > 0)) {
          return true;
        }
      }
      // Note: H200 Number and H200 PDF are now optional
    }

    // Check if markups have been reviewed for projects that require markups
    if (showMarkupsTab && !markupsReviewed) {
      return true;
    }

    return false;
  };

  const showMarkupsTab = formData.type === 'H200' || formData.type === 'CMC/MVI' || formData.type === 'Other Revenue';

  const modalFooter = (
    <>
      <Button variant="secondary" type="button" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        disabled={isSubmitDisabled()}
        onClick={handleSubmit}
      >
        {project ? 'Update' : 'Create'}
      </Button>
    </>
  );

  // Add location selection handler
  const handleLocationSelect = (lat: number, lng: number, type: 'start' | 'end') => {
    if (type === 'start') {
      setFormData(prev => ({
        ...prev,
        h200StartLatitude: lat,
        h200StartLongitude: lng
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        h200EndLatitude: lat,
        h200EndLongitude: lng
      }));
    }
  };

  // Handle asset type changes
  const handleAssetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAssetType = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      h200AssetType: newAssetType,
      h200AssetId: undefined, // Clear selected asset when type changes
      h200StartLatitude: undefined, // Clear GPS coordinates
      h200StartLongitude: undefined,
      h200EndLatitude: undefined,
      h200EndLongitude: undefined
    }));
    
    setSelectedAsset(null); // Clear selected asset
    
    // If we have a contract area selected, search for assets with the new type
    if (formData.h200ContractAreaId && newAssetType) {
      loadAssetsForContractArea(formData.h200ContractAreaId, newAssetType);
    } else {
      // Clear assets if no contract area or asset type
      setAssets([]);
      setRoadAssets([]);
    }
  };

  // Handle contract area changes
  const handleContractAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newContractAreaId = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      h200ContractAreaId: newContractAreaId ? Number(newContractAreaId) : undefined,
      h200AssetType: '', // Clear asset type when contract area changes
      h200AssetId: undefined, // Clear selected asset
      h200StartLatitude: undefined, // Clear GPS coordinates
      h200StartLongitude: undefined,
      h200EndLatitude: undefined,
      h200EndLongitude: undefined
    }));
    
    setSelectedAsset(null); // Clear selected asset
    setAssets([]); // Clear assets list
    setRoadAssets([]);

    // Reload assets for the new contract area
    if (newContractAreaId) {
      loadAssetsForContractArea(Number(newContractAreaId));
    }
  };

  return (
    <>
      {/* Debug information */}
      {process.env.NODE_ENV === 'development' && (
        <div className="tw-fixed tw-top-0 tw-left-0 tw-p-2 tw-bg-warning tw-text-black" style={{ zIndex: 9999, fontSize: '12px' }}>
          <strong>ProjectModal Debug:</strong><br />
          Visible: {visible.toString()}<br />
          Has Project: {!!project}<br />
          Project ID: {project?.id || 'None'}<br />
          Project Name: {project?.name || 'None'}<br />
          Form Data Name: "{formData.name}"<br />
          Active Tab: {activeTab}
        </div>
      )}
      
      <ResponsiveModal
        isOpen={visible}
        onClose={onClose}
        title={project ? 'Edit Project' : 'Add Project'}
        footer={modalFooter}
      >
      <form onSubmit={handleSubmit}>
        {saveError && (
          <Alert variant="danger" role="alert" className="tw-mb-3">
            {saveError}
          </Alert>
        )}
        <ul className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-border-b tw-border-line tw-list-none tw-pl-0 tw-mb-3">
          <li className="tw-inline-flex">
            <button
              type="button"
              className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'general' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
          </li>
          {showMarkupsTab && (
            <li className="tw-inline-flex">
              <button
                type="button"
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'markups' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('markups')}
              >
                Markups <span className="tw-text-danger">*</span>
              </button>
            </li>
          )}
          {formData.type === 'H200' && (
            <li className="tw-inline-flex">
              <button
                type="button"
                className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'h200' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                onClick={() => setActiveTab('h200')}
              >
                H200 Details <span className="tw-text-danger">*</span>
              </button>
            </li>
          )}

          {/* Debug information for tabs */}
          {process.env.NODE_ENV === 'development' && (
            <li className="tw-inline-flex tw-ml-auto">
              <div className="tw-px-3 tw-py-2 tw-text-content-muted" style={{ fontSize: '11px' }}>
                Debug: Active Tab: {activeTab} | Project Type: {formData.type} | Show Markups: {showMarkupsTab.toString()}
              </div>
            </li>
          )}
        </ul>

        {activeTab === 'general' && (
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-4 sm:tw-grid-cols-2">
            {/* Debug information for form data */}
            {process.env.NODE_ENV === 'development' && (
              <Form.Group style={{ gridColumn: '1 / -1' }}>
                <div className="tw-mt-2 tw-p-2 tw-bg-surface-alt tw-border tw-border-line tw-rounded" style={{ fontSize: '12px' }}>
                  <strong>Form Data Debug:</strong><br />
                  Name: "{formData.name}"<br />
                  Description: "{formData.description}"<br />
                  Status: "{formData.status}"<br />
                  Type: "{formData.type}"<br />
                  Service Area ID: {formData.serviceAreaId}<br />
                  Owner ID: {formData.ownerId}<br />
                  Budget: {formData.budget}<br />
                  Start Date: {formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : 'Not set'}<br />
                  End Date: {formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : 'Not set'}
                </div>
              </Form.Group>
            )}

            {/* Row 1: Project Name & Project Owner */}
            <Form.Group>
              <Form.Label>Project Name <span className="tw-text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Project Owner <span className="tw-text-danger">*</span></Form.Label>
              <Form.Select
                name="ownerId"
                value={formData.ownerId || ''}
                onChange={handleChange}
                required
                disabled={!formData.serviceAreaId}
              >
                <option value="">
                  {!formData.serviceAreaId
                    ? 'Select Service Area first'
                    : users.length === 0
                      ? 'No users available for this service area'
                      : 'Select Project Owner'
                  }
                </option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} {user.lastName}
                  </option>
                ))}
              </Form.Select>
              {formData.serviceAreaId && users.length > 0 && (
                <small className="tw-text-content-muted">
                  Showing {users.length} user{users.length !== 1 ? 's' : ''} for the selected service area
                </small>
              )}
            </Form.Group>

            {/* Row 2: Project Number & Project Type */}
            <Form.Group>
              <Form.Label>Project Number</Form.Label>
              <Form.Control
                type="text"
                name="projectNumber"
                value={formData.projectNumber || ''}
                readOnly
                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
              />
              <small className="tw-text-content-muted">Project number is auto-generated based on Service Area.</small>
            </Form.Group>
            <Form.Group>
              <Form.Label>Project Type <span className="tw-text-danger">*</span></Form.Label>
              <Form.Select
                name="type"
                value={formData.type || ''}
                onChange={handleChange}
                required
              >
                {PROJECT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Row 3: Service Area & Status */}
            <Form.Group>
              <Form.Label>Service Area <span className="tw-text-danger">*</span></Form.Label>
              <Form.Select
                name="serviceAreaId"
                value={formData.serviceAreaId || ''}
                onChange={handleChange}
                required
              >
                <option value="">Select Service Area</option>
                {serviceAreas.map(area => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Status <span className="tw-text-danger">*</span></Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                isInvalid={!!statusError}
                aria-invalid={!!statusError}
              >
                {/* Show all supported project statuses */}
                <option value="planned">Planned</option>
                <option value="pending">Proposed</option>
                <option value="active">Active</option>
                <option value="need_revisions">Needs Revisions</option>
                <option value="cancelled">Cancelled</option>
                {canCloseProject && <option value="completed">Completed</option>}
              </Form.Select>
              {statusError && (
                <Form.Control.Feedback type="invalid">{statusError}</Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Row 4: Job Number & Start Date */}
            <Form.Group>
              <Form.Label>Job Number</Form.Label>
              <Form.Control
                id="projectJobNumber"
                type="text"
                isInvalid={!!(formData.jobNumber && !jobNumberValidation.isValid) || !!jobNumberRequiredError}
                aria-invalid={!!(formData.jobNumber && !jobNumberValidation.isValid) || !!jobNumberRequiredError}
                name="jobNumber"
                value={formData.jobNumber || ''}
                onChange={handleChange}
                placeholder="e.g., ABC1-123-1234 or ABCD-123-1234"
                maxLength={13}
                disabled={formData.type === 'Quantified'}
                style={formData.type === 'Quantified' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : undefined}
              />
              {formData.jobNumber && !jobNumberValidation.isValid && (
                <Form.Control.Feedback type="invalid">
                  {jobNumberValidation.message}
                </Form.Control.Feedback>
              )}
              {jobNumberRequiredError && !formData.jobNumber && (
                <Form.Control.Feedback type="invalid">{jobNumberRequiredError}</Form.Control.Feedback>
              )}
              <small className="tw-text-content-muted">
                {formData.type === 'Quantified'
                  ? 'Job number is not used for Quantified projects.'
                  : 'This is the number that is used in Sage. Format: XXXx-YYY-ZZZZ where all characters must be alphanumeric and XXXx can be either 3 or 4 characters (e.g. ABC1-123-12DE or 1234-5E6-78FG)'}
              </small>
            </Form.Group>
            <Form.Group>
              <Form.Label>Start Date {formData.status === 'active' && <span className="tw-text-danger">*</span>}</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={formatDateForInput(formData.startDate)}
                onChange={handleChange}
                required={formData.status === 'active'}
              />
            </Form.Group>

            {/* Row 5: Budget & End Date */}
            <Form.Group>
              <Form.Label>Budget <span className="tw-text-danger">*</span></Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  name="budget"
                  value={formData.budget || 0}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>End Date (or best estimate) {formData.status === 'active' && <span className="tw-text-danger">*</span>}</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={formatDateForInput(formData.endDate)}
                onChange={handleChange}
                required={formData.status === 'active'}
                min={formatDateForInput(formData.startDate) || undefined}
              />
              {isDateRangeInverted && (
                <Form.Text className="tw-text-danger">
                  End date must be on or after the start date.
                </Form.Text>
              )}
            </Form.Group>

            {/* Row 6: Description (full width) */}
            <Form.Group style={{ gridColumn: '1 / -1' }}>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                rows={3}
              />
            </Form.Group>
          </div>
        )}

        {activeTab === 'markups' && showMarkupsTab && (
          <div>
            {/* Message about default markups */}
            {formData.serviceAreaId && (
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                These values are defaulted based on Service Area contract settings.
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Labour Markup (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="labourMarkup"
                    value={formData.labourMarkup}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Material Markup (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="materialMarkup"
                    value={formData.materialMarkup}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Subcontractor Markup (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="subcontractorMarkup"
                    value={formData.subcontractorMarkup}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Hired Equipment Markup (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="hiredEquipmentMarkup"
                    value={formData.hiredEquipmentMarkup}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Vehicle Markup (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="vehicleMarkup"
                    value={formData.vehicleMarkup}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </Col>
            </Row>

            {/* Mandatory checkbox for reviewing markups */}
            <div className="tw-border tw-rounded tw-p-3 tw-mt-4" style={{ backgroundColor: '#f8f9fa', borderColor: markupsReviewed ? '#28a745' : '#dc3545' }}>
              <div className="tw-flex tw-items-start tw-gap-2">
                <input
                  className="tw-shrink-0"
                  type="checkbox"
                  id="markupsReviewed"
                  checked={markupsReviewed}
                  onChange={(e) => setMarkupsReviewed(e.target.checked)}
                  required
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                    cursor: 'pointer',
                    borderWidth: '2px',
                    borderColor: '#0d6efd',
                    backgroundColor: markupsReviewed ? '#0d6efd' : 'white'
                  }}
                />
                <label
                  className="tw-font-semibold"
                  htmlFor="markupsReviewed"
                  style={{ cursor: 'pointer', fontSize: '1rem' }}
                >
                  These markup values have been reviewed and are confirmed to be correct for this project to the best of my knowledge. <span className="tw-text-danger">*</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'h200' && formData.type === 'H200' && (
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-4 sm:tw-grid-cols-2">
            {/* Debug information for H200 tab */}
            {process.env.NODE_ENV === 'development' && (
              <Form.Group style={{ gridColumn: '1 / -1' }}>
                <div className="tw-mt-2 tw-p-2 tw-bg-surface-alt tw-border tw-border-line tw-rounded" style={{ fontSize: '12px' }}>
                  <strong>H200 Tab Debug Info:</strong><br />
                  Form Data Type: {formData.type}<br />
                  H200 Activity Type: {formData.h200ActivityType || 'Not set'}<br />
                  H200 Contract Area ID: {formData.h200ContractAreaId || 'Not set'}<br />
                  H200 Asset Type: {formData.h200AssetType || 'Not set'}<br />
                  H200 Asset ID: {formData.h200AssetId || 'Not set'}<br />
                  H200 Start Lat: {formData.h200StartLatitude || 'Not set'}<br />
                  H200 Start Lng: {formData.h200StartLongitude || 'Not set'}<br />
                  H200 End Lat: {formData.h200EndLatitude || 'Not set'}<br />
                  H200 End Lng: {formData.h200EndLongitude || 'Not set'}<br />
                  Selected Asset: {selectedAsset ? `${selectedAsset.name} (${selectedAsset.assetNumber})` : 'None'}<br />
                  Total Assets Loaded: {assets.length}
                </div>
              </Form.Group>
            )}

            {/* H200 Activity Type */}
            <Form.Group>
              <Form.Label>H200 Activity Type <span className="tw-text-danger">*</span></Form.Label>
              <Form.Select
                name="h200ActivityType"
                value={formData.h200ActivityType || ''}
                onChange={handleChange}
                required
              >
                <option value="">Select H200 Activity Type</option>
                <option value="999400">#999400 - H0200 – Routine Maintenance Services above RMSC</option>
                <option value="999401">#999401 - H0200 – Major Event Response</option>
                <option value="999402">#999402 - H0200 – Maintenance Services on Class 8/F</option>
                <option value="999403">#999403 - H0200 – More Quantified</option>
                <option value="999404">#999404 - H0200 – Designated First Nations Services</option>
                <option value="999405">#999405 - H0200 – Other</option>
              </Form.Select>
            </Form.Group>

            {/* H200 Number */}
            <Form.Group>
              <Form.Label>H200 Number</Form.Label>
              <Form.Control
                type="text"
                name="h200Number"
                value={formData.h200Number || ''}
                onChange={handleChange}
                placeholder="Enter H200 number (optional)"
              />
            </Form.Group>

            <Form.Group style={{ gridColumn: '1 / -1' }}>
              <Form.Label>Does a deductible apply? (MoTT)</Form.Label>
              <Form.Check
                type="radio"
                name="h200DeductibleRadio"
                id="h200DedNo"
                label="No"
                checked={!formData.h200DeductibleApplies}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    h200DeductibleApplies: false,
                    h200DeductibleAmount: undefined
                  }))
                }
              />
              <Form.Check
                type="radio"
                name="h200DeductibleRadio"
                id="h200DedYes"
                label="Yes"
                checked={formData.h200DeductibleApplies === true}
                onChange={() => setFormData((prev) => ({ ...prev, h200DeductibleApplies: true }))}
              />
                  {formData.h200DeductibleApplies ? (
                    <div className="tw-mt-2">
                      <Form.Label htmlFor="h200DeductibleAmountInput">
                        Deductible amount <span className="tw-text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="h200DeductibleAmountInput"
                        type="number"
                        name="h200DeductibleAmount"
                        min={0.01}
                        step={0.01}
                        value={formData.h200DeductibleAmount ?? ''}
                        onChange={handleChange}
                        placeholder="e.g. 1,250.00"
                      />
                    </div>
                  ) : null}
            </Form.Group>

            {/* Contract Area */}
            <Form.Group>
              <Form.Label>
                Contract Area
                {formData.type === 'H200' && <span className="tw-text-danger">*</span>}
              </Form.Label>
              <Form.Select
                isInvalid={formData.type === 'H200' && !formData.h200ContractAreaId}
                name="h200ContractAreaId"
                value={formData.h200ContractAreaId || ''}
                onChange={handleContractAreaChange}
                required={formData.type === 'H200'}
                disabled={!formData.serviceAreaId}
              >
                <option value="">
                  {formData.serviceAreaId ? 'Select Contract Area' : 'Select a Service Area first'}
                </option>
                {h200ContractAreaOptions.map(area => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                    {!isInServiceArea(area, Number(formData.serviceAreaId)) && ' (other service area)'}
                  </option>
                ))}
              </Form.Select>
              {formData.type === 'H200' && !formData.h200ContractAreaId && (
                <Form.Control.Feedback type="invalid">
                  Contract Area is required for H200 projects.
                </Form.Control.Feedback>
              )}

              {/* Debug information for contract area */}
              {process.env.NODE_ENV === 'development' && (
                <div className="tw-mt-2 tw-p-2 tw-bg-surface-alt tw-border tw-border-line tw-rounded" style={{ fontSize: '12px' }}>
                  <strong>Contract Area Debug:</strong><br />
                  Selected Contract Area: {formData.h200ContractAreaId || 'None'}<br />
                  Total Contract Areas: {h200ContractAreaOptions.length}<br />
                  Available Contract Areas: {h200ContractAreaOptions.map(a => `${a.name} (${a.id})`).join(', ')}
                </div>
              )}
            </Form.Group>

            {/* Asset Type */}
            <Form.Group>
              <Form.Label>
                Asset Type
                {formData.type === 'H200' && <span className="tw-text-danger">*</span>}
              </Form.Label>
              <Form.Select
                isInvalid={formData.type === 'H200' && !formData.h200AssetType}
                name="h200AssetType"
                value={formData.h200AssetType || ''}
                onChange={handleAssetTypeChange}
                required={formData.type === 'H200'}
              >
                <option value="">Select Asset Type</option>
                {Object.values(ASSET_TYPES).map(type => (
                  <option key={type} value={type}>
                    {ASSET_TYPE_LABELS[type]}
                  </option>
                ))}
              </Form.Select>
              {formData.type === 'H200' && !formData.h200AssetType && (
                <Form.Control.Feedback type="invalid">
                  Asset Type is required for H200 projects.
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Asset Selection */}
            <Form.Group>
              <Form.Label>
                Asset
                {formData.type === 'H200' && <span className="tw-text-danger">*</span>}
              </Form.Label>
              <div className="tw-flex tw-gap-2">
                <div className="tw-grow">
                  <MultiTierDropdown
                    items={assets}
                    value={selectedAsset?.id}
                    onChange={(assetId) => {
                      const asset = assets.find(a => a.id === assetId);
                      if (asset) {
                        setSelectedAsset(asset);
                        
                        // Update the asset ID in form data
                        setFormData(prev => ({
                          ...prev,
                          h200AssetId: assetId
                        }));
                        
                        // Auto-populate GPS coordinates when asset is selected
                        if (asset.startLatitude && asset.startLongitude) {
                          setFormData(prev => ({
                            ...prev,
                            h200StartLatitude: asset.startLatitude,
                            h200StartLongitude: asset.startLongitude,
                            h200EndLatitude: asset.endLatitude || asset.startLatitude,
                            h200EndLongitude: asset.endLongitude || asset.startLongitude
                          }));
                        }
                      } else {
                        setSelectedAsset(null);
                        setFormData(prev => ({
                          ...prev,
                          h200AssetId: undefined
                        }));
                      }
                    }}
                    placeholder="Select an asset"
                    disabled={!formData.h200AssetType}
                    noItemsMessage={
                      !formData.h200ContractAreaId 
                        ? "Please select a contract area first" 
                        : !formData.h200AssetType 
                          ? "Please select an asset type first"
                          : assets.length === 0 
                            ? "No assets found for the selected criteria" 
                            : "No matching assets found"
                    }
                    getParentDescription={(parentAssetNumber) => {
                      const parentAsset = roadAssets.find(a => a.assetNumber === parentAssetNumber);
                      return parentAsset?.description || parentAsset?.name || parentAssetNumber;
                    }}
                  />
                </div>
                <Button
                  variant={showMap ? 'primary' : 'outline-primary'}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMap(!showMap);
                  }}
                  disabled={!formData.h200AssetType}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {showMap ? 'Hide Map' : 'Show Map'}
                </Button>
              </div>

              {/* Debug information */}
              {process.env.NODE_ENV === 'development' && (
                <div className="tw-mt-2 tw-p-2 tw-bg-surface-alt tw-border tw-border-line tw-rounded" style={{ fontSize: '12px' }}>
                  <strong>Debug Info:</strong><br />
                  Contract Area: {formData.h200ContractAreaId || 'None selected'}<br />
                  Asset Type: {formData.h200AssetType || 'None selected'}<br />
                  Total Assets: {assets.length}<br />
                  Available Asset Types: {Array.from(new Set(assets.map(a => a.assetType))).join(', ')}<br />
                  Selected Asset: {selectedAsset ? `${selectedAsset.name} (${selectedAsset.assetNumber})` : 'None'}
                </div>
              )}
            </Form.Group>

            {/* GPS Coordinates - Start and End in rows */}
            <Form.Group style={{ gridColumn: '1 / -1' }}>
              <Form.Label>
                GPS Coordinates
                {formData.type === 'H200' && <span className="tw-text-danger">*</span>}
              </Form.Label>
              <Row>
                <Col md={6}>
                  <Form.Label>Start Latitude</Form.Label>
                  <Form.Control
                    type="number"
                    isInvalid={formData.type === 'H200' && !formData.h200StartLatitude}
                    name="h200StartLatitude"
                    value={formData.h200StartLatitude || ''}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g., 49.123"
                    required={formData.type === 'H200'}
                  />
                  {formData.type === 'H200' && !formData.h200StartLatitude && (
                    <Form.Control.Feedback type="invalid">
                      Start latitude is required for H200 projects.
                    </Form.Control.Feedback>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Label>Start Longitude</Form.Label>
                  <Form.Control
                    type="number"
                    isInvalid={formData.type === 'H200' && !formData.h200StartLongitude}
                    name="h200StartLongitude"
                    value={formData.h200StartLongitude || ''}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g., -123.456"
                    required={formData.type === 'H200'}
                  />
                  {formData.type === 'H200' && !formData.h200StartLongitude && (
                    <Form.Control.Feedback type="invalid">
                      Start longitude is required for H200 projects.
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Row>
              <Row className="tw-mt-2">
                <Col md={6}>
                  <Form.Label>End Latitude (Optional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="h200EndLatitude"
                    value={formData.h200EndLatitude || ''}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g., 49.124"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>End Longitude (Optional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="h200EndLongitude"
                    value={formData.h200EndLongitude || ''}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g., -123.457"
                  />
                </Col>
              </Row>
            </Form.Group>

            {/* GPS Map Display */}
            {showMap && (formData.h200StartLatitude && formData.h200StartLongitude) && (
              <Form.Group style={{ gridColumn: '1 / -1' }}>
                <GoogleGPSMap
                  startLocation={formData.h200StartLatitude && formData.h200StartLongitude ? {
                    lat: Number(formData.h200StartLatitude),
                    lng: Number(formData.h200StartLongitude)
                  } : undefined}
                  endLocation={formData.h200EndLatitude && formData.h200EndLongitude ? {
                    lat: Number(formData.h200EndLatitude),
                    lng: Number(formData.h200EndLongitude)
                  } : undefined}
                  onLocationSelect={handleLocationSelect}
                  locationType={locationType}
                  isPointActivity={false}
                />
              </Form.Group>
            )}

            {/* H200 Form Upload */}
            <Form.Group style={{ gridColumn: '1 / -1' }}>
              <Form.Label>
                H200 Form Upload (Optional)
              </Form.Label>
                              <Form.Control
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedH200FormFile(file);
                    }
                  }}
                />
              <small className="tw-text-content-muted">Upload the ministry H200 form (PDF only) - Optional</small>

              {/* Show existing attachment if available */}
              {formData.h200FormAttachmentUrl && project?.id && (
                <div className="tw-mt-2">
                  <small className="tw-text-info">
                    <i className="bi bi-file-pdf tw-mr-1"></i>
                    Current attachment: <a href={`/api/projects/${project.id}/h200-form-download`} target="_blank" rel="noopener noreferrer">View PDF</a>
                  </small>
                  <div className="tw-mt-1">
                    <small className="tw-text-content-muted">
                      Upload a new file to replace the existing attachment
                    </small>
                  </div>
                </div>
              )}

              {/* Show newly selected file */}
              {selectedH200FormFile && (
                <div className="tw-mt-2">
                  <small className="tw-text-success">
                    <i className="bi bi-check-circle tw-mr-1"></i>
                    New file selected: {selectedH200FormFile.name}
                  </small>
                </div>
              )}

              {/* Note: H200 Form upload is now optional */}
            </Form.Group>
          </div>
        )}
      </form>
    </ResponsiveModal>
    </>
  );
};

export default ProjectModal; 